import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { HiOutlineShieldExclamation, HiX } from 'react-icons/hi'
import {
  fetchMfaStatus,
  dismissMfaPrompt,
  selectMfa,
  selectMfaStatus,
  fetchProfile,
  selectProfile,
  selectProfileStatus,
} from 'applications/Settings/settingsSlice'

/**
 * One-time, dismissible nudge to set up MFA — shown once per account until dismissed or
 * enabled. Only relevant for accounts that sign in with email/password (profile.hasPassword);
 * OAuth-only accounts don't hit the local login/MFA flow at all.
 */
export default function MfaSetupNudgeBanner() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const mfa = useSelector(selectMfa)
  const mfaStatus = useSelector(selectMfaStatus)
  const profile = useSelector(selectProfile)
  const profileStatus = useSelector(selectProfileStatus)

  useEffect(() => {
    if (mfaStatus === 'idle') {
      dispatch(fetchMfaStatus() as any)
    }
  }, [mfaStatus, dispatch])

  useEffect(() => {
    if (profileStatus === 'idle') {
      dispatch(fetchProfile() as any)
    }
  }, [profileStatus, dispatch])

  if (!profile?.hasPassword) return null
  if (!mfa?.shouldPromptSetup) return null

  const handleEnable = () => {
    dispatch(dismissMfaPrompt() as any)
    navigate('/settings?view=profile')
  }

  const handleDismiss = () => {
    dispatch(dismissMfaPrompt() as any)
  }

  return (
    <div className="tw-relative tw-flex tw-items-center tw-justify-between tw-gap-4 tw-bg-gradient-to-r tw-from-amber-50 tw-to-orange-50 tw-border tw-border-amber-200 tw-rounded-xl tw-shadow-sm tw-px-4 tw-py-3 tw-mb-4 tw-mx-4 sm:tw-mx-0">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-full tw-bg-amber-100 tw-ring-4 tw-ring-white tw-flex-shrink-0">
          <HiOutlineShieldExclamation className="tw-w-4 tw-h-4 tw-text-amber-600" />
        </div>
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-amber-900 tw-mb-0">Secure your account</p>
          <p className="tw-text-xs tw-text-amber-800/80 tw-mb-0">
            Turn on multi-factor authentication to add an extra layer of protection.
          </p>
        </div>
      </div>
      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
        <button
          type="button"
          onClick={handleEnable}
          className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-600 tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-white tw-shadow-sm hover:tw-bg-amber-700 tw-transition-colors"
        >
          Enable MFA
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
          className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-full tw-text-amber-700 hover:tw-bg-amber-100 hover:tw-text-amber-900 tw-bg-transparent tw-border-0 tw-transition-colors"
        >
          <HiX className="tw-w-4 tw-h-4" />
        </button>
      </div>
    </div>
  )
}
